import { useState, useEffect, useCallback } from "react";
import { FiChevronRight, FiX, FiAlertTriangle, FiCheck } from "react-icons/fi";
import { apiGet, apiPost, apiPut, apiDelete } from "../api";
import { PageHeader, Card, Btn, Icon } from "./KioskUI";
import BulkImport from "./BulkImport";

const CATEGORY_IMPORT_COLUMNS = [
  { key: "name", label: "Name", required: true },
  { key: "name_mm", label: "Myanmar Name" },
  { key: "code", label: "Code" },
];

/* ── code auto-suggest ── */
const suggestCode = (name) =>
  name.toUpperCase().replace(/[^A-Z0-9]+/g, "").slice(0, 12);

/* ── Add / Edit modal ── */
function CategoryModal({ existing, allCodes, onClose, onSaved, token }) {
  const isEdit = !!existing;
  const [name, setName]           = useState(existing?.name || "");
  const [nameMm, setNameMm]       = useState(existing?.name_mm || "");
  const [code, setCode]           = useState(existing?.code || "");
  const [codeTouched, setCodeTouched] = useState(isEdit);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState("");

  const onNameChange = (v) => {
    setName(v);
    if (!codeTouched) setCode(suggestCode(v));
  };
  const onCodeChange = (v) => {
    setCodeTouched(true);
    setCode(v.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 12));
  };

  const codeConflict = code.trim().length > 0 &&
    allCodes.filter(c => c !== existing?.code).includes(code.trim());
  const canSubmit = name.trim().length > 0 && code.trim().length > 0 && !codeConflict && !saving;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    setError("");
    try {
      const payload = { name: name.trim(), name_mm: nameMm.trim(), code: code.trim() };
      const res = isEdit
        ? await apiPut(`/api/inventory/categories/${existing.id}`, payload, token)
        : await apiPost("/api/inventory/categories", payload, token);
      if (res.ok) {
        const data = await res.json();
        onSaved(data, !isEdit);
      } else {
        const err = await res.json().catch(() => ({}));
        setError(err.error || err.detail || "Failed to save category.");
      }
    } catch {
      setError("Cannot connect to server.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(19,24,32,.42)", backdropFilter: "blur(2px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 60 }}>
      <form onClick={e => e.stopPropagation()} onSubmit={handleSubmit}
        style={{ background: "var(--surface)", borderRadius: "var(--r-xl)", boxShadow: "0 24px 70px rgba(19,24,32,.3)", width: 480, maxWidth: "94vw", padding: "24px 26px 22px", display: "flex", flexDirection: "column" }}>

        {/* Modal header */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 14, marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 13 }}>
            <div style={{ width: 44, height: 44, borderRadius: 13, background: "var(--blue-50)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Icon name="categories" size={21} stroke="var(--primary)" />
            </div>
            <div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 9 }}>
                <span style={{ fontFamily: "var(--font-head)", fontSize: 20, fontWeight: 800, color: "var(--ink)", letterSpacing: "-.02em" }}>
                  {isEdit ? "Edit Category" : "Add Category"}
                </span>
                <span style={{ fontFamily: "var(--font-mm)", fontSize: 14, color: "var(--n-400)" }}>
                  {isEdit ? "အမျိုးအစားပြင်ဆင်" : "အမျိုးအစားအသစ်"}
                </span>
              </div>
              <div style={{ fontSize: 13, color: "var(--sub)", marginTop: 3 }}>
                Group products so they're easy to find and filter.
              </div>
            </div>
          </div>
          <button type="button" onClick={onClose} style={{ border: "1px solid var(--line)", background: "var(--surface)", borderRadius: "var(--r-sm)", padding: 7, cursor: "pointer", display: "flex", flexShrink: 0 }}>
            <FiX size={16} color="var(--sub)" />
          </button>
        </div>

        {/* Fields */}
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {/* Category name */}
          <ModalField label="Category name" mm="အမျိုးအစားအမည်" required>
            <input autoFocus value={name} onChange={e => onNameChange(e.target.value)}
              placeholder="e.g. T-Shirts" style={inputStyle} />
          </ModalField>

          {/* Category code */}
          <div>
            <ModalField label="Category code" hint="auto-filled · editable" required>
              <input value={code} onChange={e => onCodeChange(e.target.value)}
                placeholder="e.g. TEES" style={{ ...inputStyle, fontVariantNumeric: "tabular-nums", letterSpacing: ".04em" }} />
            </ModalField>
            {codeConflict ? (
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 7, fontSize: 12, fontWeight: 600, color: "var(--danger)" }}>
                <FiAlertTriangle size={13} /> "{code}" is already used by another category.
              </div>
            ) : (
              <div style={{ marginTop: 6, fontSize: 12, color: "var(--n-400)", lineHeight: 1.45 }}>
                Short code products are tagged with. Auto-suggested from name — type to override.
              </div>
            )}
          </div>

          {/* Myanmar name */}
          <ModalField label="Myanmar name" mm="မြန်မာနာမည်" hint="optional">
            <input value={nameMm} onChange={e => setNameMm(e.target.value)}
              placeholder="e.g. တီရှပ်"
              style={{ ...inputStyle, fontFamily: "var(--font-mm)" }} />
          </ModalField>
        </div>

        {error && (
          <div style={{ marginTop: 12, fontSize: 13, color: "var(--danger)", padding: "8px 12px", background: "var(--danger-bg)", borderRadius: "var(--r-sm)" }}>
            {error}
          </div>
        )}

        {/* Footer */}
        <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
          <Btn kind="ghost" onClick={onClose} style={{ flex: 1, justifyContent: "center" }}>Cancel</Btn>
          <button type="submit" disabled={!canSubmit}
            style={{ flex: 1, justifyContent: "center", display: "inline-flex", alignItems: "center", gap: 8, fontFamily: "var(--font-head)", fontWeight: 700, fontSize: 14.5, borderRadius: "var(--r-sm)", padding: "11px 18px", border: "none", cursor: canSubmit ? "pointer" : "not-allowed", background: canSubmit ? "var(--primary)" : "var(--n-200)", color: "#fff", transition: "background .12s" }}>
            <FiCheck size={16} />
            {saving ? "Saving…" : isEdit ? "Save Changes" : "Add Category"}
          </button>
        </div>
      </form>
    </div>
  );
}

function ModalField({ label, mm, hint, required, children }) {
  return (
    <div>
      <label style={{ display: "flex", gap: 7, alignItems: "baseline", fontSize: 12.5, fontWeight: 700, color: "var(--sub)", letterSpacing: ".02em", marginBottom: 7 }}>
        {label}
        {required && <span style={{ color: "var(--danger)", fontWeight: 700 }}>·</span>}
        {mm && <span style={{ fontFamily: "var(--font-mm)", fontWeight: 500, color: "var(--n-400)" }}>{mm}</span>}
        {hint && <span style={{ fontWeight: 500, color: "var(--n-400)" }}>{hint}</span>}
      </label>
      <div style={{ display: "flex", alignItems: "center", border: "1px solid var(--line)", borderRadius: "var(--r-sm)", padding: "10px 13px", background: "var(--surface)" }}>
        {children}
      </div>
    </div>
  );
}

const inputStyle = {
  border: "none", outline: "none", background: "transparent",
  width: "100%", fontFamily: "var(--font-body)", fontSize: 14.5, color: "var(--ink)",
};

/* ── Category card ── */
function CategoryCard({ cat, onEdit, onDelete }) {
  const [hover, setHover] = useState(false);
  return (
    <Card
      style={{ padding: "20px 22px", cursor: "pointer", border: `1px solid ${hover ? "var(--blue-100)" : "var(--line)"}`, background: hover ? "var(--faint)" : "var(--surface)", transition: "border-color .12s, background .12s" }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {/* Top row: icon tile + chevron */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div style={{ width: 42, height: 42, borderRadius: 12, background: "var(--blue-50)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Icon name="categories" size={20} stroke="var(--primary)" />
        </div>
        <FiChevronRight size={18} color="var(--n-300)" />
      </div>

      {/* Name */}
      <div style={{ fontFamily: "var(--font-head)", fontSize: 17, fontWeight: 700, color: "var(--ink)", marginTop: 16 }}>
        {cat.name}
      </div>

      {/* Myanmar name */}
      {cat.name_mm && (
        <div style={{ fontFamily: "var(--font-mm)", fontSize: 13.5, color: "var(--sub)", marginTop: 3 }}>
          {cat.name_mm}
        </div>
      )}

      {/* Bottom row: product count + code chip */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginTop: 12 }}>
        <div style={{ fontSize: 13, color: "var(--sub)", fontWeight: 600 }}>
          {cat.product_count ?? 0} product{cat.product_count !== 1 ? "s" : ""}
        </div>
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".08em", color: "var(--n-500)", background: "var(--faint)", border: "1px solid var(--line)", borderRadius: 6, padding: "3px 8px", fontVariantNumeric: "tabular-nums" }}>
          {String(cat.code).toUpperCase()}
        </span>
      </div>

      {/* Edit / Delete actions (shown below on hover or always) */}
      <div style={{ display: "flex", gap: 8, marginTop: 14, paddingTop: 12, borderTop: "1px solid var(--hair)" }}>
        <button onClick={e => { e.stopPropagation(); onEdit(cat); }}
          style={{ flex: 1, padding: "7px 0", fontFamily: "var(--font-body)", fontSize: 13, fontWeight: 600, color: "var(--n-600)", background: "var(--n-50)", border: "1px solid var(--line)", borderRadius: "var(--r-sm)", cursor: "pointer" }}>
          Edit
        </button>
        <button onClick={e => { e.stopPropagation(); onDelete(cat); }}
          style={{ flex: 1, padding: "7px 0", fontFamily: "var(--font-body)", fontSize: 13, fontWeight: 600, color: "var(--danger)", background: "var(--danger-bg)", border: "1px solid transparent", borderRadius: "var(--r-sm)", cursor: "pointer" }}>
          Delete
        </button>
      </div>
    </Card>
  );
}

/* ── Confirm delete modal ── */
function ConfirmDeleteModal({ cat, onCancel, onConfirm }) {
  return (
    <div onClick={onCancel} style={{ position: "fixed", inset: 0, background: "rgba(19,24,32,.42)", backdropFilter: "blur(2px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 60 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "var(--surface)", borderRadius: "var(--r-xl)", boxShadow: "0 24px 70px rgba(19,24,32,.3)", width: 380, padding: "26px 26px 22px" }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 13, marginBottom: 14 }}>
          <div style={{ width: 42, height: 42, borderRadius: 12, background: "var(--danger-bg)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <Icon name="trash" size={20} stroke="var(--danger)" />
          </div>
          <div>
            <div style={{ fontFamily: "var(--font-head)", fontSize: 17, fontWeight: 800, color: "var(--ink)" }}>Delete "{cat.name}"?</div>
            <div style={{ fontSize: 13.5, color: "var(--sub)", marginTop: 4, lineHeight: 1.5 }}>
              This cannot be undone. If the category has products, deletion will be blocked.
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
          <Btn kind="ghost" onClick={onCancel} style={{ flex: 1, justifyContent: "center" }}>Cancel</Btn>
          <button onClick={onConfirm} style={{ flex: 1, justifyContent: "center", display: "inline-flex", alignItems: "center", gap: 8, fontFamily: "var(--font-head)", fontWeight: 700, fontSize: 14.5, borderRadius: "var(--r-sm)", padding: "11px 18px", border: "none", cursor: "pointer", background: "var(--danger)", color: "#fff" }}>
            <Icon name="trash" size={16} stroke="#fff" /> Delete
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Main screen ── */
export default function Categories({ token, go }) {
  const [categories, setCategories]   = useState([]);
  const [loading, setLoading]         = useState(true);
  const [modal, setModal]             = useState(null); // null | { mode:'add' } | { mode:'edit', cat } | { mode:'delete', cat }
  const [importing, setImporting]     = useState(false);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await apiGet("/api/inventory/categories", token);
      if (res.ok) {
        const data = await res.json();
        setCategories(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  const allCodes = categories.map(c => c.code);

  const handleSaved = (data, isNew) => {
    if (isNew) {
      setCategories(prev => [{ ...data, isNew: true }, ...prev]);
    } else {
      setCategories(prev => prev.map(c => c.id === data.id ? data : c));
    }
    setModal(null);
  };

  const handleDelete = async (cat) => {
    try {
      const res = await apiDelete(`/api/inventory/categories/${cat.id}`, token);
      if (res.ok) {
        setCategories(prev => prev.filter(c => c.id !== cat.id));
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.error || "Cannot delete this category.");
      }
    } catch {
      alert("Cannot connect to server.");
    }
    setModal(null);
  };

  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 240 }}>
        <span style={{ color: "var(--sub)", fontSize: 14 }}>Loading…</span>
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="Categories" mm="အမျိုးအစား" desc={`${categories.length} categor${categories.length !== 1 ? "ies" : "y"}`}>
        <Btn kind="ghost" onClick={() => setImporting(true)}>Import</Btn>
        <Btn kind="pri" icon="plus" onClick={() => setModal({ mode: "add" })}>Add Category</Btn>
      </PageHeader>

      {importing && (
        <BulkImport
          title="Categories"
          columns={CATEGORY_IMPORT_COLUMNS}
          endpoint="/api/inventory/categories/bulk"
          token={token}
          onClose={() => setImporting(false)}
          onDone={fetchCategories}
        />
      )}

      {categories.length === 0 ? (
        <div style={{ textAlign: "center", color: "var(--n-400)", fontSize: 14, padding: "48px 0" }}>
          No categories yet. Add one to get started.
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16 }}>
          {categories.map(cat => (
            <CategoryCard
              key={cat.id}
              cat={cat}
              onEdit={cat => setModal({ mode: "edit", cat })}
              onDelete={cat => setModal({ mode: "delete", cat })}
            />
          ))}
        </div>
      )}

      {/* Add / Edit modal */}
      {(modal?.mode === "add" || modal?.mode === "edit") && (
        <CategoryModal
          existing={modal.mode === "edit" ? modal.cat : null}
          allCodes={allCodes}
          onClose={() => setModal(null)}
          onSaved={handleSaved}
          token={token}
        />
      )}

      {/* Delete confirmation */}
      {modal?.mode === "delete" && (
        <ConfirmDeleteModal
          cat={modal.cat}
          onCancel={() => setModal(null)}
          onConfirm={() => handleDelete(modal.cat)}
        />
      )}
    </div>
  );
}
